<template>
  <div class="graph-attr-list-wrap">
  <div v-if="loading" class="graph-attr-list__status">加载属性中...</div>
  <p v-else-if="error" class="graph-attr-list__status">{{ error }}</p>
  <dl v-else class="graph-attr-list">
    <template v-for="field in fields">
      <div
        v-if="field.isMedia && field.imageUrls && field.imageUrls.length"
        :key="`media-${field.id}`"
        class="graph-attr-list__media"
      >
        <dt>{{ field.label }}</dt>
        <div class="graph-attr-list__images">
          <img
            v-for="(url, index) in field.imageUrls"
            :key="`${field.id}-${index}`"
            :src="url"
            :alt="field.label"
          />
        </div>
      </div>
      <div
        v-else-if="isSourceField(field) && sourceLinks(field).length"
        :key="`source-${field.id || field.label}`"
        class="graph-attr-list__row"
      >
        <dt>{{ field.label }}：</dt>
        <dd class="graph-attr-list__source">
          <template v-for="(link, index) in sourceLinks(field)">
            <router-link
              v-if="link.resourceId"
              :key="`${field.label}-${link.resourceId}-${index}`"
              :to="`/resources/${link.resourceId}`"
              class="graph-attr-list__link"
              @click.native.stop
            >
              {{ link.text }}
            </router-link>
            <span v-else :key="`${field.label}-text-${index}`">{{ link.text }}</span>
            <span
              v-if="index < sourceLinks(field).length - 1"
              :key="`${field.label}-sep-${index}`"
            >、</span>
          </template>
        </dd>
      </div>
      <div
        v-else-if="field.value"
        :key="field.id || field.label"
        class="graph-attr-list__row"
      >
        <dt>{{ field.label }}：</dt>
        <dd>{{ field.value }}</dd>
      </div>
    </template>
    <p v-if="!fields.length" class="graph-attr-list__status">暂无属性数据</p>
  </dl>
  </div>
</template>

<script>
import { fetchEntityRelatedResources } from '@/api/knowledge'
import { fetchResourcesByIds, mapResourceItem, searchResources } from '@/api/resources'
import {
  isSourceField,
  matchResourceBySourceName,
  resolveSourceLinks,
  sourceSearchKeyword
} from '@/utils/entityMeta'

export default {
  name: 'GraphAttributeList',
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    entityId: {
      type: [Number, String],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      relatedResources: [],
      sourceResources: [],
      resolvedSourceLinks: {}
    }
  },
  computed: {
    resourceLookup() {
      const map = new Map()
      ;[...this.relatedResources, ...this.sourceResources].forEach((item) => {
        if (item && item.id != null) {
          map.set(Number(item.id), item)
        }
      })
      return Array.from(map.values())
    }
  },
  watch: {
    entityId: {
      immediate: true,
      handler(id) {
        this.loadRelatedResources(id)
      }
    },
    fields: {
      deep: true,
      handler() {
        this.resolveSourceFields()
      }
    }
  },
  methods: {
    isSourceField,
    sourceLinks(field) {
      const key = field && (field.id || field.label)
      if (key && this.resolvedSourceLinks[key] && this.resolvedSourceLinks[key].length) {
        return this.resolvedSourceLinks[key]
      }
      return resolveSourceLinks(field, this.resourceLookup)
    },
    async loadRelatedResources(entityId) {
      if (!entityId) {
        this.relatedResources = []
        this.resolveSourceFields()
        return
      }
      try {
        const { records } = await fetchEntityRelatedResources({
          entityId,
          page: 1,
          limit: 50
        })
        this.relatedResources = records || []
      } catch (error) {
        this.relatedResources = []
      }
      this.resolveSourceFields()
    },
    async resolveSourceFields() {
      const next = {}
      const pendingSearches = []
      const idSet = new Set()

      this.fields.forEach((field) => {
        if (!this.isSourceField(field)) {
          return
        }
        const key = field.id || field.label
        const links = resolveSourceLinks(field, this.resourceLookup).map((link) => ({ ...link }))
        links.forEach((link, index) => {
          if (link.resourceId) {
            idSet.add(Number(link.resourceId))
            return
          }
          const matched = matchResourceBySourceName(link.text, this.resourceLookup)
          if (matched && matched.id) {
            link.resourceId = matched.id
            link.text = matched.title || link.text
            idSet.add(Number(matched.id))
            return
          }
          pendingSearches.push({ key, index, text: link.text })
        })
        next[key] = links
      })

      if (idSet.size) {
        const missingIds = [...idSet].filter((id) => (
          !this.relatedResources.some((item) => Number(item.id) === id)
          && !this.sourceResources.some((item) => Number(item.id) === id)
        ))
        if (missingIds.length) {
          try {
            const records = await fetchResourcesByIds(missingIds)
            const mapped = (records || []).map(mapResourceItem)
            this.sourceResources = [...this.sourceResources, ...mapped]
            Object.keys(next).forEach((label) => {
              next[label] = next[label].map((link) => {
                if (!link.resourceId) return link
                const found = mapped.find((item) => Number(item.id) === Number(link.resourceId))
                return found ? { ...link, text: found.title || link.text } : link
              })
            })
          } catch (error) {
            // ignore
          }
        }
      }

      if (pendingSearches.length) {
        const keywordMap = new Map()
        pendingSearches.forEach((item) => {
          const keyword = sourceSearchKeyword(item.text)
          if (!keyword) return
          if (!keywordMap.has(keyword)) {
            keywordMap.set(keyword, [])
          }
          keywordMap.get(keyword).push(item)
        })

        await Promise.all([...keywordMap.entries()].map(async ([keyword, items]) => {
          try {
            const { records } = await searchResources({ page: 1, limit: 10, keyword })
            const mapped = (records || []).map(mapResourceItem)
            if (mapped.length) {
              this.sourceResources = [...this.sourceResources, ...mapped]
            }
            items.forEach((item) => {
              const matched = matchResourceBySourceName(item.text, mapped)
                || matchResourceBySourceName(item.text, this.resourceLookup)
              if (!matched || !matched.id) return
              const links = next[item.key]
              if (!links || !links[item.index]) return
              links[item.index] = {
                text: matched.title || item.text,
                resourceId: matched.id
              }
            })
          } catch (error) {
            // ignore search failures
          }
        }))
      }

      this.resolvedSourceLinks = next
    }
  }
}
</script>

<style lang="scss" scoped>
.graph-attr-list-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.graph-attr-list {
  display: flex;
  flex-direction: column;
  gap: rem(10);

  &__status {
    font-size: rem(13);
    color: $color-text-light;
    line-height: 1.7;
  }

  &__row {
    display: flex;
    font-size: rem(13);
    line-height: 1.7;

    dt {
      flex-shrink: 0;
      color: $color-text-light;
    }

    dd {
      color: $color-brown-dark;
      word-break: break-all;
    }
  }

  &__source {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0 2px;
  }

  &__link {
    color: $color-brown-dark;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover {
      color: $success-color;
    }
  }

  &__media dt {
    display: block;
    font-size: rem(12);
    color: $color-text-light;
    margin-bottom: rem(6);
  }

  &__images {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: rem(8);

    img {
      width: 100%;
      height: rem(72);
      object-fit: cover;
      border-radius: 4px;
    }
  }
}
</style>
